import { sampleSkyAuthoredPose, skyAuthoredPoseContact } from './115-sky-authored-pose.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';

const roles = new WeakMap();
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const point = (s) => ({ x: s.x, y: s.y, z: s.pilotZ ?? SKY_WORLD.pilotZ });
export const skyTwinDistance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/** Surface ownership is compiled from the original one-weight source triangles.
 * The small reactor patch belongs to the actual body beneath the animated canopy;
 * it is a triangle selection, never a substitute collision sphere. */
function surfaces(rig) {
  if (roles.has(rig)) return roles.get(rig);
  const result = new Map(),
    rest = sampleSkyAuthoredPose(rig, 'Idle', 0),
    core = rest.sockets.reactor_target;
  for (let m = 0; m < rig.meshes.length; m++) {
    const mesh = rig.meshes[m],
      v = rest.meshes[m].vertices;
    if (!mesh.jointNodes) continue;
    for (let t = 0; t < mesh.indices.length; t += 3) {
      const ids = mesh.indices.slice(t, t + 3);
      const names = ids.map((id) => {
        let slot = 0;
        for (let n = 1; n < 4; n++)
          if (mesh.weights[id * 4 + n] > mesh.weights[id * 4 + slot]) slot = n;
        return rig.nodes[mesh.jointNodes[mesh.joints[id * 4 + slot]]].name;
      });
      let role = names.every((n) => n === names[0]) ? names[0] : null;
      if (role === 'body_joint' && core) {
        const c = { x: 0, y: 0, z: 0 };
        for (const id of ids)
          for (const [axis, k] of ['x', 'y', 'z'].entries()) c[k] += v[id * 3 + axis] / 3;
        role = skyTwinDistance(c, core) <= 2 ? 'reactor' : role;
      }
      result.set(`${m}:${t / 3}`, role);
    }
  }
  roles.set(rig, result);
  return result;
}
export function skyTwinSurfaceRole(item, contact) {
  return surfaces(item.authoredRig).get(`${contact.meshIndex}:${contact.triangleIndex}`) ?? null;
}

/** Metre-scale cable follows real emitter sockets. A wrap requires a nearest
 * triangle hit on a copper horn, then a new physical pilot displacement which
 * bends the cable around that retained source contact. Time cannot create a wrap. */
export function stepSkyTwinTether(tether, gantry, from, to, pilot, dt) {
  tether.from = { ...from };
  tether.to = { ...to };
  if (tether.broken) return false;
  if (!tether.knot) {
    const hit = skyAuthoredPoseContact(gantry.authoredPose, from, to);
    const role = hit && skyTwinSurfaceRole(gantry, hit);
    if (!['horn_left_joint', 'horn_right_joint'].includes(role)) return false;
    tether.knot = { x: hit.x, y: hit.y, z: hit.z };
    tether.horn = role === 'horn_left_joint' ? 'left' : 'right';
    tether.capturedPilot = { ...pilot };
    tether.captureLength = skyTwinDistance(from, hit) + skyTwinDistance(hit, to);
    tether.tension = 0;
  }
  const a = skyTwinDistance(from, tether.knot),
    b = skyTwinDistance(to, tether.knot),
    direct = skyTwinDistance(from, to);
  const detour = a + b - direct;
  const movement = skyTwinDistance(pilot, tether.capturedPilot);
  tether.tension = clamp(detour / 2.5, 0, 1);
  tether.movement = movement;
  if (detour > 2.5 && movement > 4) {
    tether.broken = true;
    tether.brokeAt = tether.age ?? 0;
    return true;
  }
  tether.age = (tether.age ?? 0) + dt;
  return false;
}

/** Fixed shipyard controls retain physical world X/Y and the normal forward
 * cannon plane. The source gantry owns the arena's metre envelope; neither the
 * camera nor the donor craft can teleport the pilot through a structure. */
export function stepSkyTwinPilot(state, input, dt, { speed, hit }) {
  const twins = state.boss?.twins;
  if (!twins) return false;
  const before = point(state),
    limit = twins.lateralLimit;
  const dx = Number.isFinite(input.targetX)
    ? clamp((clamp(input.targetX, -limit, limit) - state.x) * 0.12, -speed * dt, speed * dt)
    : clamp(Number.isFinite(input.x) ? input.x : 0, -1, 1) * speed * dt;
  const next = {
    x: clamp(state.x + dx, -limit, limit),
    y: state.y + clamp(Number.isFinite(input.y) ? input.y : 0, -1, 1) * 9 * dt,
    z: before.z,
  };
  next.y = clamp(
    next.y,
    skyTerrainHeight(next.x, next.z, state.terrainTravel) + SKY_WORLD.pilotAimHeight,
    state.boss.authoredFlightCeiling,
  );
  const contact = skyAuthoredPoseContact(
    twins.gantry.authoredPose,
    before,
    next,
    null,
    SKY_WORLD.pilotContactRadius,
  );
  if (contact) hit?.(state, 'gantry');
  else {
    state.x = next.x;
    state.y = next.y;
  }
  state.locomotion = 'fly';
  return true;
}

/** The exported gantry certifies a40m-wide,26m-high clear aperture. Route a
 * whole28m original craft through its centre before allowing lateral movement;
 * front/rear holding lanes stay beyond the source's deepest surfaces. */
export function skyTwinCraftWaypoint(position, goal, rig, gantry) {
  const bounds = gantry.authoredPose.bounds;
  const depth = Math.max(Math.abs(rig.bounds.min[2]), Math.abs(rig.bounds.max[2])) + 2;
  const near = bounds.max[2] + depth,
    far = bounds.min[2] - depth;
  const crossing =
    (position[2] < near && position[2] > far) ||
    (position[2] >= near && goal[2] < near) ||
    (position[2] <= far && goal[2] > far);
  if (!crossing) return goal;
  const height = Math.max(16, Math.min(24, goal[1]));
  if (Math.abs(position[0]) > 0.15 || Math.abs(position[1] - height) > 0.15)
    return [0, height, position[2]];
  return [0, height, goal[2] >= near ? near + 0.5 : goal[2] <= far ? far - 0.5 : goal[2]];
}

export const SKY_TWIN_TEACHING = Object.freeze({
  status: 'partial',
  summary:
    'Original moving-gantry aperture traversal; tether threading is introduced in the encounter',
});
/** Optional source-backed approach practice. The same complete gantry travels
 * past the real pilot; exact relative-motion sphere/triangle contact owns damage.
 * Only a clean traversal of its measured aperture earns one finite award. */
export function stepSkyTwinLesson(state, dt, travel, owners) {
  if (state.startMode === 'boss' || state.boss || state.journey?.phase === 'boss') {
    if (state.twinLesson) state.twinLesson.active = false;
    return;
  }
  if (state.status !== 'playing' || !Number.isFinite(dt) || dt <= 0 || !Number.isFinite(travel))
    return;
  if (state.twinLesson?.sector !== state.sector) {
    const rig = state.authoredPropRigs?.['twin-gantry'];
    if (!rig) return;
    const rootTransform = { position: [0, 0, -160], quaternion: [0, 0, 0, 1], scale: [1, 1, 1] };
    const pose = sampleSkyAuthoredPose(rig, 'Traverse', 0, rootTransform);
    const prop = {
      id: state.nextId++,
      kind: 'authored-prop',
      artVariant: 'twin-gantry',
      authoredRig: rig,
      authoredPose: pose,
      rootTransform,
      x: 0,
      y: 0,
      z: -160,
      hp: 1,
      age: 0,
      collisionEnabled: false,
    };
    state.twinLesson = {
      sector: state.sector,
      age: 0,
      distance: 0,
      props: [prop],
      pilot: point(state),
      previousCenter: pose.sockets.aperture_center,
      active: true,
      complete: false,
      hit: false,
      entered: false,
      earned: false,
      salvage: 0,
      flightCeiling: pose.sockets.aperture_center.y + 12,
    };
  }
  const lesson = state.twinLesson;
  if (lesson.complete) return;
  lesson.age += dt;
  lesson.distance += travel;
  const prop = lesson.props[0];
  prop.rootTransform.position[2] = -160 + lesson.distance;
  prop.z = prop.rootTransform.position[2];
  prop.age = lesson.age;
  prop.authoredPose = sampleSkyAuthoredPose(
    prop.authoredRig,
    'Traverse',
    lesson.age % 6,
    prop.rootTransform,
  );
  const center = prop.authoredPose.sockets.aperture_center,
    now = point(state);
  lesson.navigationTarget = { ...center };
  const previous = Object.fromEntries(
    ['x', 'y', 'z'].map((k) => [k, lesson.pilot[k] + center[k] - lesson.previousCenter[k]]),
  );
  const hit = skyAuthoredPoseContact(
    prop.authoredPose,
    previous,
    now,
    null,
    SKY_WORLD.pilotContactRadius,
  );
  if (hit && !lesson.hit) {
    lesson.hit = true;
    owners.hit?.(state, 'gantry-lesson');
  }
  if (lesson.previousCenter.z < now.z && center.z >= now.z)
    lesson.entered = Math.abs(now.x - center.x) < 18 && now.y > 16 && now.y < 38;
  if (prop.authoredPose.bounds.min[2] > now.z + 2) {
    lesson.complete = true;
    lesson.earned = lesson.entered && !lesson.hit;
    if (lesson.earned) {
      state.score += 250;
      lesson.salvage = 1;
      state.approachSkillSalvage = (state.approachSkillSalvage ?? 0) + 1;
      owners.earnSalvage(state, 1);
      owners.burst(state, now, 'cyan', 'impact', { scoreAward: 250, combo: 1 });
      owners.event(state, 'twin-gantry-clear', 'SHIPYARD PASSAGE CLEARED · +250');
    } else owners.event(state, 'twin-gantry-miss', 'READ THE REAL GANTRY OPENING · KEEP FLYING');
  }
  lesson.pilot = now;
  lesson.previousCenter = { ...center };
}
export function skyTwinLessonTelemetry(state) {
  const lesson = state.twinLesson;
  if (!lesson || lesson.sector !== state.sector || state.boss) return null;
  return {
    text: lesson.complete
      ? 'SHIPYARD APPROACH · WATCH THE COPPER HORNS'
      : `GANTRY PASSAGE · CLIMB TO ${Math.round(lesson.navigationTarget?.y ?? 24)}m`,
    label: lesson.complete
      ? `${lesson.earned ? 'Clean original gantry passage.' : 'Gantry passage missed.'} Tether threading is introduced by the guardian.`
      : 'Climb through the original moving gantry opening. Steer away from the real side columns.',
    progress: Math.min(1, lesson.distance / 180),
    complete: lesson.complete,
  };
}
